import { useEffect, useState } from "react";

import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Hairline } from "../../components/Hairline";
import { Input } from "../../components/Input";
import { Label } from "../../components/Label";
import { Steps } from "../../components/Steps";

import BannersVisibility from "./BannersVisibility";

import styles from "../../../styles/banners.module.scss";

import { Upload } from "../../components/Icons";

import { If } from "shared/components/control-statements";
import { Banners } from "shared/core/Banners";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { AssetsAPI } from "../../../providers/AssetsAPI";

const validateImageSize = async (file, width, height, isCabana, isCiaTC) => {
  if (!file) {
    throw new Error("Arquivo inválido.");
  }

  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const widthImage = img.naturalWidth;
      const heightImage = img.naturalHeight;

      if (
        (widthImage !== width || heightImage !== height) &&
        !isCabana &&
        !isCiaTC
      ) {
        reject(new Error(`A imagem deve ter a dimensão ${width}x${height}`));
      }
      resolve(file);
    };
  });
};

const BannersForm = ({
  visible,
  selectedBanner,
  dimensions,
  category,
  device,
  onClose,
  onSave,
  onDelete,
}) => {
  const controller = new Banners();

  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(selectedBanner || {});
  const [currentStep, setCurrentStep] = useState(1);
  const [holdingId, setHoldingId] = useState(null);

  const getHoldingId = async () => {
    const holdingId = await EnvironmentStorage.getEnvironmentHoldingId();
    setHoldingId(holdingId);
  };

  useEffect(() => {
    getHoldingId();
  }, []);

  const environment = EnvironmentStorage.getEnvironment();

  const isCabana =
    environment?.brand?.alias === "cabana-burger779" ||
    environment?.alias === "cabana-burger779";

  const isCiaTC = holdingId && holdingId === 811;

  useEffect(() => {
    setBanner(selectedBanner);
  }, [selectedBanner]);

  const sendFile = async (file) => {
    const maxFileSize = 1500;
    const fileSize = file.size / 1024;
    const fileType = file.type;

    if (fileSize > maxFileSize) {
      alert({ text: `Escolha um arquivo com no máximo ${maxFileSize} KB.` });
      return false;
    }

    const pattern = /^(image\/(png|jpeg|svg\+xml))$/i;

    if (!fileType.match(pattern)) {
      alert({
        text: "Arquivo inválido. Utilize algum dos seguintes formatos: .png, .jpg, .jpeg, .svg",
      });
      return false;
    }

    return await new Promise((resolve) => {
      let reader = new FileReader();
      reader.onload = async (readerEvent) => {
        const response = await AssetsAPI.PostAssets({
          mime_type: fileType,
          file: readerEvent.target.result.split("base64,")[1],
        });
        resolve(response.getData(null));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSetImage = async (e) => {
    setLoading(true);
    const file = e.target.files[0];
    try {
      const res = await validateImageSize(
        file,
        device === "mobile"
          ? dimensions.mobile.width
          : dimensions.desktop.width,
        device === "mobile"
          ? dimensions.mobile.height
          : dimensions.desktop.height,
        isCabana,
        isCiaTC
      );
      const uri = await sendFile(res);
      setBanner({ ...banner, image: uri });
    } catch (err) {
      alert({ text: err.message });
    }
    setLoading(false);
  };

  const getDimensions = () => {
    if (device === "mobile") {
      return `(${dimensions.mobile.width}x${dimensions.mobile.height})`;
    }

    if (device === "desktop") {
      return `(${dimensions.desktop.width}x${dimensions.desktop.height})`;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const success = await controller.upsert(
      category,
      device,
      banner,
      isCiaTC ? holdingId : null
    );
    setLoading(false);
    if (success === true) {
      onSave && onSave();
    }
  };

  const handleDelete = async () => {
    const confirmed = await window.confirm({
      text: "Você realmente deseja excluir esse banner?",
      confirmText: "Sim",
      cancelText: "Voltar",
    });

    if (!confirmed) {
      return false;
    }

    const success = await controller.delete(
      category,
      device,
      banner.id,
      isCiaTC ? holdingId : null
    );
    if (success === true) {
      onDelete && onDelete();
    }
  };

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose && onClose();
        setCurrentStep(1);
      }}
      title="Banners"
      showBackButton={currentStep === 2}
      backButton={() => setCurrentStep(1)}
    >
      <Steps steps={2} currentStep={currentStep} />

      <div className={styles.scrollContainer}>
        {currentStep === 1 && (
          <>
            <label htmlFor="upload" className={styles.upload}>
              <If cond={loading}>
                <div className={styles.loadingBox}>
                  <span className={styles.loader}></span>
                </div>
              </If>
              <If cond={!loading}>
                <If cond={banner?.image}>
                  <img
                    src={`${process.env.REACT_APP_ASSETS_URL}/${banner?.image}`}
                    class="w-100"
                  />
                </If>
                <If cond={!banner?.image}>
                  <div className={styles.icon}>
                    <Upload />
                  </div>
                  <div className={styles.selectFile}>
                    <p>Selecione o arquivo</p>
                    <span>&nbsp;para fazer upload</span>
                  </div>
                  <span className={styles.fileAccepted}>
                    JPG, JPEG, PNG, SVG {getDimensions()}
                  </span>
                  <span className={styles.fileAccepted}>
                    Peso máximo do arquivo: 1,5MB
                  </span>
                </If>
              </If>
              <input
                type="file"
                id="upload"
                accept=".png, .jpeg, .jpg, .svg"
                onChange={(e) => handleSetImage(e)}
              />
            </label>

            <Hairline />

            <Label>Defina o nome do Banner</Label>
            <Input
              placeholder="Ex. Banner 1"
              value={banner?.name || ""}
              onChange={(e) => setBanner({ ...banner, name: e.target.value })}
            />

            <Hairline />

            <Label>Link do Banner</Label>
            <Input
              value={banner?.link || ""}
              onChange={(e) => setBanner({ ...banner, link: e.target.value })}
            />
            <If cond={banner?.id}>
              <div className="d-flex justify-center">
                <Button
                  design="underline"
                  onClick={handleDelete}
                  className="mt-30 mb-10"
                  disabled={loading}
                >
                  Remover banner
                </Button>
              </div>
            </If>
          </>
        )}

        {currentStep === 2 && (
          <BannersVisibility
            banner={banner}
            alwaysAvailable={() => setBanner({ ...banner, visibility: null })}
            onChange={({ visibility }) => setBanner({ ...banner, visibility })}
            benefitAlwaysAvailable={!banner?.visibility}
          />
        )}
      </div>

      <div className={styles.footer}>
        <If cond={currentStep === 1}>
          <Button
            design="primary"
            className="w-100"
            disabled={loading || !banner?.image}
            loading={loading}
            onClick={() => setCurrentStep(2)}
          >
            Próximo
          </Button>
        </If>
        <If cond={currentStep === 2}>
          <Button
            design="primary"
            className="w-100"
            disabled={loading || banner?.visibility?.length === 0}
            loading={loading}
            onClick={() => handleSubmit()}
          >
            Salvar
          </Button>
        </If>
      </div>
    </Drawer>
  );
};

export default BannersForm;
